/**
 * One image thumbnail with its four controls: annotate, delete, the
 * `#H1-2` label, and "insert a reference at the caret".
 *
 * Shared by the Notes and Trade Journal drawers. They had grown two
 * near-identical copies of this markup and two sets of CSS that had
 * already drifted apart — different sizes, offsets and centring — so
 * the same control looked worse in one drawer than the other. One
 * component, one stylesheet, no way for that to happen again.
 */
import { AttachmentLabel } from "@/components/AttachmentLabel/AttachmentLabel";
import styles from "./AttachmentThumb.module.css";

export function AttachmentThumb({
  url,
  filename,
  bytes,
  label,
  /** What a reference to this image reads like in the field that
   *  cites it — "the note body", "the reason". Tooltips only. */
  citedIn,
  onOpen,
  onAnnotate,
  onDelete,
  onRename,
  onInsertRef,
}: {
  url: string;
  filename: string;
  bytes: number;
  label: string;
  citedIn: string;
  onOpen: () => void;
  onAnnotate: () => void;
  onDelete: () => void;
  onRename: (next: string) => void;
  onInsertRef: () => void;
}) {
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    fn();
  };

  return (
    <div
      className={styles.thumb}
      onClick={onOpen}
      title={`${filename} (${(bytes / 1024).toFixed(0)} KB)`}
    >
      <img src={url} alt={filename} loading="lazy" decoding="async" />
      <button
        type="button"
        className={styles.edit}
        onClick={stop(onAnnotate)}
        title="Annotate this image (pen + text)"
        aria-label="Annotate image"
      >
        ✎
      </button>
      <button
        type="button"
        className={styles.remove}
        onClick={stop(onDelete)}
        title="Delete this image"
        aria-label="Delete image"
      >
        ✕
      </button>
      <AttachmentLabel
        label={label}
        onRename={onRename}
        hint={`Image #${label} — click to rename. Cite it from ${citedIn} as #${label}.`}
      />
      <button
        type="button"
        className={styles.insert}
        // Keeps focus — and therefore the caret — in the editor. A
        // blur here fires the save, and the response would overwrite
        // the field through its sync effect, swallowing the token
        // just inserted.
        onMouseDown={(e) => e.preventDefault()}
        onClick={stop(onInsertRef)}
        title={`Insert [#${label}] into ${citedIn} at the cursor`}
        aria-label={`Insert a reference to image ${label}`}
      >
        +#
      </button>
    </div>
  );
}
